/**
 * AddKartotekaFieldModal — Etap 2 (E2.2b).
 *
 * Modal „Dodaj pole" do kartoteki systemowej z panelu super admina (ITE), wbudowany
 * w edytor szablonu modułu (zakładka „Pola" encji-odwołania).
 *
 * Model TYLKO-DODAJĄCY (decyzja Eugenii 2026-06-23 + 2026-06-25): super admin może
 * WYŁĄCZNIE dodać nowe pole. Pole staje się systemowe — propaguje się do wszystkich
 * tenantów i jest u nich zablokowane (klient nie usunie ani nie zmieni). Nie ma edycji
 * ani usuwania istniejących pól (brak takich akcji w UI; backend też ich nie udostępnia).
 *
 * Backend: POST `/api/admin/kartoteki/:slug/fields` (E2.1, add-only; permission
 * `module_builder.designer`). Różni się od tenantowego `AddSystemFieldModal`:
 *  - scope admina (cookie session), inny endpoint;
 *  - prostszy zestaw pól (bez isUnique/searchable/filterable/showInList — to flagi tenantowe);
 *  - 13 typów whitelisted (SYSTEM_EXT_FIELD_TYPES), bo pole = kolumna w ext-tabeli.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SYSTEM_EXT_FIELD_TYPES, type SystemExtFieldType } from '@saas/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { DictionaryParamBindingsEditor } from '@/components/dictionaries/DictionaryParamBindingsEditor';
import { GlobalDictionaryPicker } from '@/components/dictionaries/GlobalDictionaryPicker';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import type { DictionaryParamBindingsMap } from '@/hooks/useDictionary';
import { api } from '@/lib/api';
import { useDictionarySources, useGlobalDictionaries } from '@/lib/global-dictionaries/api';
import { useKartotekaDefinition } from '@/lib/kartoteki/kartoteka-definition';

const SLUG_REGEX = /^[a-z][a-z0-9_]{0,62}$/;

/** Typy pola dla których pokazujemy picker słownika (opcje listy pochodzą ze słownika). */
const DICTIONARY_CAPABLE_TYPES: ReadonlySet<SystemExtFieldType> = new Set([
  'select',
  'multiselect',
]);

function nameToSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 63) || 'pole'
  );
}

interface FormState {
  name: string;
  slug: string;
  slugTouched: boolean;
  fieldType: SystemExtFieldType;
  description: string;
  isRequired: boolean;
  dictionaryCode: string | null;
  /** Nadpisanie sposobu wyświetlania pozycji słownika dla TEGO pola (Etap 1c) —
   *  `null` = użyj `dictionary.displayMode` (fallback). Ma sens tylko gdy
   *  `dictionaryCode` jest ustawiony. */
  dictionaryDisplayMode: 'label' | 'code' | 'code_label' | null;
  /**
   * Wiązania parametrów słownika zewnętrznego (Etap 10.2/10.5, ADR-0066 §11; Faza 2b
   * ADR-0068 — tagowane: pole rekordu / firma / user / stała). `null` = brak wiązań.
   */
  dictionaryParamBindings: DictionaryParamBindingsMap | null;
  /**
   * Slug kartoteki docelowej relacji (F.REF v1) — ma sens tylko dla
   * `fieldType === 'reference'`. Kreator oferuje 6 opcji: 5 kartotek płaskich
   * (`contractors` domyślna, `employees`, `users`, `products`, `companies` — każda ma
   * `coreFieldMap` w adapterze) + `documents` (F20 — JEDEN generyczny token dla
   * dowolnej z 7 klas dokumentów, klasa wybierana w runtime wewnątrz
   * `RecordTablePicker`) — wszystkie wymagane przez `RecordTablePicker`, patrz
   * `lib/system-entity-adapters.ts`. Mirror `AddSystemFieldModal` (wersja tenantowa
   * tego samego kreatora pola).
   */
  relationSystemModuleSlug: string | null;
}

const INITIAL_FORM: FormState = {
  name: '',
  slug: '',
  slugTouched: false,
  fieldType: 'varchar',
  description: '',
  isRequired: false,
  dictionaryCode: null,
  dictionaryDisplayMode: null,
  dictionaryParamBindings: null,
  relationSystemModuleSlug: null,
};

/** Domyślny target oferowany w kreatorze (pierwsza kartoteka płaska), domyślnie wybrany. */
const REFERENCE_TARGET_CONTRACTORS = 'contractors';
/** Pozostałe 4 kartoteki płaskie — każda ma `coreFieldMap` w adapterze (`lib/system-entity-adapters.ts`). */
const REFERENCE_TARGET_EMPLOYEES = 'employees';
const REFERENCE_TARGET_USERS = 'users';
const REFERENCE_TARGET_PRODUCTS = 'products';
const REFERENCE_TARGET_COMPANIES = 'companies';
/** F20 — szósty target: JEDEN generyczny token dla dowolnej z 7 klas dokumentów (klasa
 *  wybierana w runtime wewnątrz `RecordTablePicker`, patrz `lib/system-entity-adapters.ts`). */
const REFERENCE_TARGET_DOCUMENTS = 'documents';

function resolveSlug(f: FormState): string {
  return f.slugTouched ? f.slug : nameToSlug(f.name);
}

/**
 * Mapuje kod błędu z backendu na czytelny komunikat (i18n).
 * Domyślnie — generyczny komunikat błędu.
 */
function errorMessageKey(code: string | undefined): string {
  switch (code) {
    case 'FIELD_SLUG_RESERVED':
      return 'moduleTemplateEditor.referencePanel.addFieldModal.errors.reserved';
    case 'FIELD_SLUG_DUPLICATE':
      return 'moduleTemplateEditor.referencePanel.addFieldModal.errors.duplicate';
    case 'INVALID_FIELD_TYPE':
      return 'moduleTemplateEditor.referencePanel.addFieldModal.errors.invalidType';
    case 'INVALID_SLUG':
      return 'moduleTemplateEditor.referencePanel.addFieldModal.errors.invalidSlug';
    case 'FIELD_LIMIT_EXCEEDED':
      return 'moduleTemplateEditor.referencePanel.addFieldModal.errors.limit';
    default:
      return 'moduleTemplateEditor.referencePanel.addFieldModal.errors.generic';
  }
}

interface AddKartotekaFieldModalProps {
  open: boolean;
  onClose: () => void;
  /** Slug kartoteki systemowej (np. 'contractors', 'employees'). */
  kartotekaSlug: string;
  /**
   * Gdy ustawione → pole tworzone jako PRYWATNE dla tego systemowego typu dokumentu
   * (drzewko klasa → typ). Pominięte → pole WSPÓLNE klasy.
   */
  documentTypeId?: string;
}

export function AddKartotekaFieldModal({
  open,
  onClose,
  kartotekaSlug,
  documentTypeId,
}: AddKartotekaFieldModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // Reset stanu przy każdym otwarciu.
  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setErrorKey(null);
    }
  }, [open]);

  const effectiveSlug = resolveSlug(form);
  const slugValid = SLUG_REGEX.test(effectiveSlug);

  // Pola-sąsiedzi tej samej kartoteki (core + dodane) — kandydaci do wiązania
  // parametrów `record_field` (Etap 10.5). W kontekście typu dorzucamy pola dziedziczone.
  const definitionQuery = useKartotekaDefinition(kartotekaSlug, documentTypeId);
  const siblingFields = (definitionQuery.data?.fields ?? []).map((f) => ({
    slug: f.slug,
    name: f.name,
  }));

  // Panel ITE widzi tylko katalog globalny — deklaracje parametrów pochodzą z rejestru
  // źródeł (`useDictionarySources`), dopasowanego po `sourceKey` wybranego słownika
  // (`useGlobalDictionaries`), a dla źródeł SQL z `sourceParams` samego słownika.
  const { data: globalDictionaries } = useGlobalDictionaries(false);
  const { data: dictionarySources } = useDictionarySources();
  const selectedDict = form.dictionaryCode
    ? globalDictionaries?.find((d) => d.code === form.dictionaryCode)
    : undefined;
  const selectedSource =
    selectedDict?.sourceKind === 'external' && selectedDict.sourceKey
      ? dictionarySources?.find((s) => s.key === selectedDict.sourceKey)
      : undefined;
  const declaredParams = selectedSource?.params ?? selectedDict?.sourceParams ?? [];

  const createMutation = useMutation({
    mutationFn: async (input: FormState) => {
      const res = await api.post<{ field: { id: string; slug: string; name: string } }>(
        `/admin/kartoteki/${kartotekaSlug}/fields`,
        {
          name: input.name.trim(),
          slug: resolveSlug(input).trim(),
          fieldType: input.fieldType,
          description: input.description.trim() === '' ? null : input.description.trim(),
          isRequired: input.isRequired,
          dictionaryCode: DICTIONARY_CAPABLE_TYPES.has(input.fieldType)
            ? input.dictionaryCode
            : null,
          dictionaryDisplayMode:
            DICTIONARY_CAPABLE_TYPES.has(input.fieldType) && input.dictionaryCode
              ? input.dictionaryDisplayMode
              : null,
          dictionaryParamBindings:
            DICTIONARY_CAPABLE_TYPES.has(input.fieldType) && input.dictionaryCode
              ? input.dictionaryParamBindings
              : null,
          // Pole prywatne typu dokumentu (drzewko klasa → typ); pominięte = pole wspólne klasy.
          ...(documentTypeId ? { documentTypeId } : {}),
          // Cel relacji tylko dla pola typu 'reference' — dla pozostałych typów nie
          // dokładamy tych kluczy do payloadu (są bez znaczenia; backend i tak je ignoruje).
          // v1: zawsze single-select (kreator nie oferuje wyboru wielokrotności) —
          // patrz komentarz przy `relationMultiple` w `ExtFieldValueInputField`.
          ...(input.fieldType === 'reference'
            ? {
                relationSystemModuleSlug:
                  input.relationSystemModuleSlug ?? REFERENCE_TARGET_CONTRACTORS,
                relationMultiple: false,
              }
            : {}),
        },
      );
      return res.data.field;
    },
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('moduleTemplateEditor.referencePanel.addFieldModal.success'),
      });
      // Odśwież zarówno bogatą definicję (lista pól z flagami), jak i podsumowanie kartoteki.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['admin', 'kartoteki', kartotekaSlug, 'definition'],
        }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'kartoteki', kartotekaSlug] }),
      ]);
      onClose();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      setErrorKey(errorMessageKey(code));
    },
  });

  const canSubmit =
    form.name.trim().length > 0 &&
    slugValid &&
    !createMutation.isPending &&
    (form.fieldType !== 'reference' || !!form.relationSystemModuleSlug);

  const handleSubmit = () => {
    if (!canSubmit) return;
    setErrorKey(null);
    createMutation.mutate(form);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('moduleTemplateEditor.referencePanel.addFieldModal.title')}
      description={t('moduleTemplateEditor.referencePanel.addFieldModal.description')}
      size="md"
    >
      <div className="space-y-4">
        <FormField label={t('moduleTemplateEditor.referencePanel.addFieldModal.name')} required>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t('moduleTemplateEditor.referencePanel.addFieldModal.namePlaceholder')}
            maxLength={100}
            autoFocus
            data-testid="add-kartoteka-field-name"
          />
        </FormField>

        <FormField
          label={t('moduleTemplateEditor.referencePanel.addFieldModal.slug')}
          hint={
            slugValid
              ? t('moduleTemplateEditor.referencePanel.addFieldModal.slugHint')
              : t('moduleTemplateEditor.referencePanel.addFieldModal.slugInvalid')
          }
          required
        >
          <Input
            value={effectiveSlug}
            onChange={(e) => setForm({ ...form, slug: e.target.value, slugTouched: true })}
            placeholder="np. numer_krs"
            maxLength={63}
            data-testid="add-kartoteka-field-slug"
          />
        </FormField>

        <FormField
          label={t('moduleTemplateEditor.referencePanel.addFieldModal.fieldType')}
          required
        >
          <Dropdown
            value={form.fieldType}
            onChange={(v) => {
              const nextType = v as SystemExtFieldType;
              setForm({
                ...form,
                fieldType: nextType,
                // Auto-select domyślnej opcji (contractors) przy przełączeniu na 'reference'
                // — mirror `AddSystemFieldModal` (zachowuje ręczny wybór, np. documents).
                relationSystemModuleSlug:
                  nextType === 'reference'
                    ? (form.relationSystemModuleSlug ?? REFERENCE_TARGET_CONTRACTORS)
                    : form.relationSystemModuleSlug,
              });
            }}
            options={SYSTEM_EXT_FIELD_TYPES.map((type) => ({
              value: type,
              label: t(`tenant.systemFieldTypes.${type}`),
            }))}
            data-testid="add-kartoteka-field-type"
          />
        </FormField>

        {form.fieldType === 'reference' && (
          <FormField
            label={t('moduleTemplateEditor.referencePanel.addFieldModal.referenceTarget')}
            required
          >
            <Dropdown
              value={form.relationSystemModuleSlug ?? REFERENCE_TARGET_CONTRACTORS}
              onChange={(v) => setForm({ ...form, relationSystemModuleSlug: v })}
              options={[
                {
                  value: REFERENCE_TARGET_CONTRACTORS,
                  label: t(
                    'moduleTemplateEditor.referencePanel.addFieldModal.referenceTargetContractors',
                  ),
                },
                {
                  value: REFERENCE_TARGET_EMPLOYEES,
                  label: t(
                    'moduleTemplateEditor.referencePanel.addFieldModal.referenceTargetEmployees',
                  ),
                },
                {
                  value: REFERENCE_TARGET_USERS,
                  label: t(
                    'moduleTemplateEditor.referencePanel.addFieldModal.referenceTargetUsers',
                  ),
                },
                {
                  value: REFERENCE_TARGET_PRODUCTS,
                  label: t(
                    'moduleTemplateEditor.referencePanel.addFieldModal.referenceTargetProducts',
                  ),
                },
                {
                  value: REFERENCE_TARGET_COMPANIES,
                  label: t(
                    'moduleTemplateEditor.referencePanel.addFieldModal.referenceTargetCompanies',
                  ),
                },
                {
                  value: REFERENCE_TARGET_DOCUMENTS,
                  label: t(
                    'moduleTemplateEditor.referencePanel.addFieldModal.referenceTargetDocuments',
                  ),
                },
              ]}
              data-testid="add-kartoteka-field-reference-target"
            />
          </FormField>
        )}

        {DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && (
          <GlobalDictionaryPicker
            value={form.dictionaryCode}
            onChange={(code) =>
              setForm({ ...form, dictionaryCode: code, dictionaryParamBindings: null })
            }
            data-testid="add-kartoteka-field-dictionary"
          />
        )}

        {DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && form.dictionaryCode && (
          <FormField label={t('common.dictionaryDisplayModeOverride.label')}>
            <Dropdown
              value={form.dictionaryDisplayMode ?? ''}
              onChange={(v) =>
                setForm({
                  ...form,
                  dictionaryDisplayMode: (v || null) as FormState['dictionaryDisplayMode'],
                })
              }
              options={[
                {
                  value: '',
                  label: t('common.dictionaryDisplayModeOverride.useDictionaryDefault'),
                },
                { value: 'label', label: t('common.dictionaryDisplayModeOverride.options.label') },
                { value: 'code', label: t('common.dictionaryDisplayModeOverride.options.code') },
                {
                  value: 'code_label',
                  label: t('common.dictionaryDisplayModeOverride.options.code_label'),
                },
              ]}
              data-testid="add-kartoteka-field-dictionary-display-mode"
            />
          </FormField>
        )}

        {DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && form.dictionaryCode && (
          <DictionaryParamBindingsEditor
            params={declaredParams}
            siblingFields={siblingFields}
            value={form.dictionaryParamBindings}
            onChange={(bindings) => setForm({ ...form, dictionaryParamBindings: bindings })}
            data-testid="add-kartoteka-field-param-bindings"
          />
        )}

        <FormField label={t('moduleTemplateEditor.referencePanel.addFieldModal.descriptionLabel')}>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            maxLength={500}
            data-testid="add-kartoteka-field-description"
          />
        </FormField>

        <Checkbox
          checked={form.isRequired}
          onChange={(e) => setForm({ ...form, isRequired: e.target.checked })}
          label={t('moduleTemplateEditor.referencePanel.addFieldModal.required')}
          data-testid="add-kartoteka-field-required"
        />

        {errorKey && (
          <p
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
            data-testid="add-kartoteka-field-error"
          >
            {t(errorKey)}
          </p>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={createMutation.isPending}
          disabled={!canSubmit}
          data-testid="add-kartoteka-field-submit"
        >
          {t('moduleTemplateEditor.referencePanel.addFieldModal.submit')}
        </Button>
      </div>
    </Modal>
  );
}
